import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Observable, Subscription } from 'rxjs';

import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BurrellesDeletionEnum } from '../../enums/burrelles-deletion-reason.enum';
import { Mention } from '../../../../Common/Mentions/models/mention';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';


@Component({
    selector: 'agpr-mention-delete-dialog',
    templateUrl: './mention-delete-dialog.component.html',
    styleUrls: ['./mention-delete-dialog.component.scss'],
    standalone: false
})
export class MentionDeleteDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() id: string;
    @Input() dialogHeader: string;
    @Input() dialogMessage: string;
    @Input() dialogConfirmMsg: string;
    @Input() folderNames$: Observable<string[]>;
    @Input() selectedMentions: Mention[] = [];
    @Input() itemCount: number;

    selectedBurrellesReason: DropdownOptionModel<BurrellesDeletionEnum> = null;
    burrellesReasonOtherText: string = '';
    selectedMentionsFolderNames: string[] = [];
    showBurrellesReason: boolean = false;
    disableSubmit: boolean = false;
    hasArchivedCriticalMention: boolean = false;

    private subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly severity = messageSeverityEnum;
    readonly burrellesDeletionEnum = BurrellesDeletionEnum;
    readonly burrellesDeletionOptions: DropdownOptionModel<BurrellesDeletionEnum>[] = [
        new DropdownOptionModel(`Mention.delete.burrelles.${BurrellesDeletionEnum.doesNotMatchInstructions}`, BurrellesDeletionEnum.doesNotMatchInstructions),
        new DropdownOptionModel(`Mention.delete.burrelles.${BurrellesDeletionEnum.duplicate}`,                BurrellesDeletionEnum.duplicate),
        new DropdownOptionModel(`Mention.delete.burrelles.${BurrellesDeletionEnum.clipQuality}`,              BurrellesDeletionEnum.clipQuality),
        new DropdownOptionModel(`Mention.delete.burrelles.${BurrellesDeletionEnum.housekeeping}`,             BurrellesDeletionEnum.housekeeping),
        new DropdownOptionModel(`Mention.delete.burrelles.${BurrellesDeletionEnum.lateClip}`,                 BurrellesDeletionEnum.lateClip),
        new DropdownOptionModel(`Mention.delete.burrelles.${BurrellesDeletionEnum.noLongerSearchTerm}`,       BurrellesDeletionEnum.noLongerSearchTerm),
        new DropdownOptionModel(`Mention.delete.burrelles.${BurrellesDeletionEnum.other}`,                    BurrellesDeletionEnum.other)
    ];

    constructor(
        public coreDialogService: CoreDialogService,
        private configurationService: ConfigService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'MentionDelete');
    }

    ngOnInit() {
        if (this.folderNames$) {
            this.subscriptions.push(
                this.folderNames$?.subscribe(folderNames => {
                    this.selectedMentionsFolderNames = folderNames;
                })
            );
        }

        const accountIsBurrelles = this.configurationService.brandGUIDIsBurrelles();
        const anyMentionIsBurrelles = this.selectedMentions.some(mention => mention.isBurrelles);
        this.showBurrellesReason = accountIsBurrelles && anyMentionIsBurrelles;
        this.disableSubmit = this.showBurrellesReason;
        this.hasArchivedCriticalMention = this.selectedMentions.some(mention => mention.isArchivedCriticalMention);
    }

    burrellesOptionChange(event: { originalEvent: Event; value: DropdownOptionModel<BurrellesDeletionEnum> }): void {
        const option = event.value.value;
        if (option === BurrellesDeletionEnum.other) {
            this.disableSubmit = this.burrellesReasonOtherText.trim().length === 0;
        } else {
            this.disableSubmit = false;
        }
    }

    burrellesOtherTextChange(value: string) {
        if (this.selectedBurrellesReason.value === BurrellesDeletionEnum.other) {
            this.disableSubmit = value.trim().length === 0;
        }
    }

    /**
     *  Action when user clicks "Delete From Account" button
     */
    deleteFromAccount(): void {
        const reason = this.getDeletionReason();
        this.closeDialog({ closeAction: true, reason: reason });
        this.selectedMentionsFolderNames = [];
    }

    /**
     *  Action when user clicks "Cancel" button, or closes the dialog via the "X" in the header
     */
    cancel(): void {
        this.closeDialog({ closeAction: false });
        this.selectedMentionsFolderNames = [];
    }

    private getDeletionReason(): string | undefined {
        if (!this.selectedBurrellesReason) {
            return undefined;
        } else if (this.selectedBurrellesReason.value === BurrellesDeletionEnum.other) {
            return this.burrellesReasonOtherText;
        } else if (this.selectedBurrellesReason.value === BurrellesDeletionEnum.doesNotMatchInstructions) {
            let returnString = this.localizationService.transform(this.selectedBurrellesReason.label);
            if (this.burrellesReasonOtherText.length) {
                returnString += ': ' + this.burrellesReasonOtherText;
            }
            // returns "Does not match instructions" or "Does not match instructions: {user entered reason}"
            return returnString;
        } else {
            return this.localizationService.transform(this.selectedBurrellesReason.label);
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
